import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { TenantService } from './services/tenant.service';
import { Tenant, TenantRequest } from './models/tenant.model';
import { AdminAggregates } from './models/admin-aggregates.model';
import { Grid } from '../../../shared/components/grid/grid';
import { GridPage } from '../../../shared/components/grid/grid-page';
import { GridDetailDirective } from '../../../shared/components/grid/directives/grid-detail.directive';
import { GridColumn, GridRowAction, GridSort, GridTab, gridColumns } from '../../../shared/components/grid/models/grid.model';
import { ToastService } from '../../../shared/services/toast-service';
import { ModalService } from '../../../shared/components/modal/modal.service';
import { LanguageService } from '../../../core/i18n/language.service';
import { BreadcrumbService } from '../../../shared/components/breadcrumbs/breadcrumb.service';
import { CreateTenantDialogComponent } from './create-tenant-dialog/create-tenant-dialog.component';
import { EditTenantDialogComponent } from './edit-tenant-dialog/edit-tenant-dialog.component';
import { InviteMemberDialogComponent } from './invite-member-dialog/invite-member-dialog.component';
import { LabelBadge } from '../../../shared/components/label-badge/label-badge';
import { Button } from '../../../shared/components/button/button';

/** Status-filter tab ids for the tenants grid. */
type TenantStatusTab = 'all' | 'enabled' | 'disabled';

/**
 * Cross-tenant administration overview (/admin/tenants) — super-admin only.
 * Implements the design's tenants-overview page: KPI strip, filterable grid,
 * and a needs-attention/activity rail. Where the backend has no data yet
 * (per-tenant user/display counts, invites, alerts, activity) the UI shows
 * explicit placeholders — see ~/work/core/back-end-TODO.md.
 */
@Component({
  selector: 'app-admin-tenants',
  standalone: true,
  imports: [
    DatePipe,
    Grid,
    GridPage,
    GridDetailDirective,
    TranslocoDirective,
    CreateTenantDialogComponent,
    EditTenantDialogComponent,
    InviteMemberDialogComponent,
    LabelBadge,
  
    Button,
  ],
  templateUrl: './tenants.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminTenantsComponent implements OnInit {
  private readonly service = inject(TenantService);
  private readonly toastService = inject(ToastService);
  private readonly modal = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly breadcrumbs = inject(BreadcrumbService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly isCreateDialogOpen = signal(false);
  readonly editingTenantId = signal<string | null>(null);
  readonly invitingTenant = signal<Tenant | null>(null);

  readonly tenants = signal<Tenant[]>([]);
  readonly page = signal(0);
  readonly size = signal(20);
  readonly totalElements = signal(0);
  readonly totalPages = signal(0);
  readonly sort = signal<GridSort>({ sortBy: 'name', sortDirection: 'ASC' });
  readonly isLoading = signal(false);

  // Search + status filter are server-side (`?search=` matches name/domains —
  // NOT alias, a Keycloak limitation; `?enabled=` totals are exact).
  readonly searchTerm = signal('');
  readonly statusTab = signal<TenantStatusTab>('all');

  // KPI strip — cross-tenant totals from /admin/aggregates; null until loaded
  // (cards render bones while the fetch is live, a dash once it settled
  // without data).
  readonly aggregates = signal<AdminAggregates | null>(null);
  readonly isLoadingAggregates = signal(false);
  readonly tenantsTotal = computed(() => {
    const agg = this.aggregates();
    if (agg) return agg.tenants.total;
    return this.totalElements() || this.tenants().length;
  });

  readonly statusTabs = computed<GridTab[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    return (['all', 'enabled', 'disabled'] as const).map((id) => ({
      id,
      label: this.transloco.translate(`admin.tenants.overview.tabs.${id}`),
    }));
  });

  /** Stable row id for tracking. */
  readonly tenantId = (tenant: Tenant) => tenant.id;
  /** Narrows the erased-generic row that grid actions hand back. */
  readonly asTenant = (row: unknown): Tenant => row as Tenant;

  /** Columns as a computed so labels re-translate on a language switch. */
  readonly columns = computed<GridColumn<Tenant>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`admin.tenants.columns.${k}`);
    // Users/Displays render a dash — per-tenant counts aren't on the list
    // payload yet (see core's back-end TODO). Alias/domains live in the drawer.
    const dash = this.transloco.translate('admin.tenants.overview.dash');
    return gridColumns<Tenant>([
      { key: 'name', label: t('name'), type: 'text', textVariant: 'strong', sortable: true, alwaysVisible: true },
      {
        key: 'enabled',
        label: t('status'),
        type: 'status',
        sortable: true,
        width: 160,
        status: (value) =>
          value
            ? { label: this.transloco.translate('admin.tenants.status.enabled'), color: 'green' }
            : { label: this.transloco.translate('admin.tenants.status.disabled'), color: 'neutral', hollow: true },
      },
      { key: 'users', label: t('users'), type: 'text', textVariant: 'muted', sortable: false, width: 120, value: () => dash },
      { key: 'displays', label: t('displays'), type: 'text', textVariant: 'muted', sortable: false, width: 120, value: () => dash },
    ]);
  });

  /** Row actions — everything lives in the ⋯ overflow menu. */
  readonly rowActions = computed<GridRowAction<Tenant>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`admin.tenants.actions.${k}`);
    const s = (k: string) => this.transloco.translate(`admin.tenants.sections.${k}`);
    return [
      { id: 'viewUsers', label: t('viewUsers'), icon: 'icon-users', placement: 'menu', section: s('users') },
      { id: 'invite', label: t('invite'), icon: 'icon-user-invite', placement: 'menu', section: s('users') },
      { id: 'edit', label: t('edit'), icon: 'icon-edit-pencil', placement: 'menu', section: s('tenant') },
      { id: 'view', label: t('view'), icon: 'icon-view-eye', placement: 'menu', section: s('tenant') },
      { id: 'disable', label: t('disable'), icon: 'icon-disable-ban', placement: 'menu', danger: true },
      { id: 'delete', label: t('delete'), icon: 'icon-delete-trash', placement: 'menu', danger: true },
    ];
  });

  ngOnInit(): void {
    this.fetch();
    this.fetchAggregates();
  }

  fetch(): void {
    this.isLoading.set(true);
    const tab = this.statusTab();
    this.service
      .getTenants(this.page(), this.size(), {
        search: this.searchTerm().trim() || undefined,
        enabled: tab === 'all' ? undefined : tab === 'enabled',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.tenants.set(res.data);
          this.totalElements.set(res.totalElements);
          this.totalPages.set(res.totalPages);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.transloco.translate('admin.tenants.toast.loadFailed'), 'error');
          this.isLoading.set(false);
        },
      });
  }

  /** KPI-strip totals. Errors are silent — the cards just fall back to
   *  their dashes (never stuck on skeleton bones). */
  private fetchAggregates(): void {
    this.isLoadingAggregates.set(true);
    this.service
      .getAggregates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (agg) => {
          this.aggregates.set(agg);
          this.isLoadingAggregates.set(false);
        },
        error: () => this.isLoadingAggregates.set(false),
      });
  }

  onSortChange(sort: GridSort): void {
    this.sort.set(sort);
    this.page.set(0);
    this.fetch();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.fetch();
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
    this.fetch();
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.page.set(0);
    this.fetch();
  }

  onTabChange(tabId: string): void {
    this.statusTab.set(tabId as TenantStatusTab);
    this.page.set(0);
    this.fetch();
  }

  openCreateDialog(): void {
    this.isCreateDialogOpen.set(true);
  }

  closeCreateDialog(): void {
    this.isCreateDialogOpen.set(false);
  }

  handleCreate(req: TenantRequest): void {
    this.service
      .createTenant(req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('admin.tenants.toast.created'), 'success');
          this.closeCreateDialog();
          this.page.set(0);
          this.fetch();
          this.fetchAggregates();
        },
        error: () => this.toastService.show(this.transloco.translate('admin.tenants.toast.createFailed'), 'error'),
      });
  }

  onRowAction(actionId: string, tenant: Tenant): void {
    if (actionId === 'edit') {
      this.editingTenantId.set(tenant.id);
    } else if (actionId === 'delete') {
      void this.confirmDelete(tenant);
    } else if (actionId === 'view') {
      this.openDetail(tenant);
    } else if (actionId === 'viewUsers') {
      this.openDetail(tenant, 'users');
    } else if (actionId === 'invite') {
      this.invitingTenant.set(tenant);
    } else {
      // disable — placeholder until implemented (the detail page has the real toggle).
      this.toastService.show(this.transloco.translate('admin.tenants.toast.notImplemented'), 'warning');
    }
  }

  closeInviteDialog(): void {
    this.invitingTenant.set(null);
  }

  onInviteSent(): void {
    this.closeInviteDialog();
    this.fetchAggregates(); // pending-invite count changed
  }

  /** Drill into the tenant detail view (overview tab, or straight to users).
   *  Names the crumb before navigating so the trail never flashes a raw id. */
  openDetail(tenant: Tenant, tab?: 'users'): void {
    this.breadcrumbs.setLabel('tenantId', tenant.id, tenant.name);
    void this.router.navigate(tab ? [tenant.id, tab] : [tenant.id], { relativeTo: this.route });
  }

  closeEditDialog(): void {
    this.editingTenantId.set(null);
  }

  handleUpdated(): void {
    this.closeEditDialog();
    this.fetch();
    this.fetchAggregates(); // the edit may have flipped enabled
  }

  private async confirmDelete(tenant: Tenant): Promise<void> {
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.transloco.translate('admin.tenants.delete.title'),
      message: this.transloco.translate('admin.tenants.delete.message', { name: tenant.name }),
      confirmLabel: this.transloco.translate('common.delete'),
      confirmWord: tenant.alias,
    });
    if (confirmed) this.deleteTenant(tenant);
  }

  private deleteTenant(tenant: Tenant): void {
    this.service
      .deleteTenant(tenant.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('admin.tenants.toast.deleted'), 'success');
          this.fetch();
          this.fetchAggregates();
        },
        error: () => this.toastService.show(this.transloco.translate('admin.tenants.toast.deleteFailed'), 'error'),
      });
  }
}
